import { useState } from 'react';
import { Button, Icon, SearchBar } from '@ab-inbev-maz/compass';
import type { IconName } from '@ab-inbev-maz/compass/dist/entities/components/icon.js';
import { useCanvasEditor } from './CanvasEditorContext';
import CanvasWorkspaceTab from './CanvasWorkspaceTab';
import CanvasTextTab from './CanvasTextTab';
import CanvasAnimationTab from './CanvasAnimationTab';
import CanvasLayersTab from './CanvasLayersTab';
import CanvasTemplatesTab from './CanvasTemplatesTab';
import CanvasSkusTab from './CanvasSkusTab';

interface RailItem {
  id: string;
  label: string;
  panelTitle: string;
  icon: IconName;
}

const RAIL_ITEMS: RailItem[] = [
  { id: 'template', label: 'Template', panelTitle: 'Templates', icon: 'media/image-add' },
  { id: 'sku', label: 'SKU', panelTitle: 'SKU', icon: 'shopping/mix-and-match' },
  { id: 'texto', label: 'Texto', panelTitle: 'Texto', icon: 'functionalities/text' },
  { id: 'animacion', label: 'Animación', panelTitle: 'Animación', icon: 'media/play' },
  { id: 'capas', label: 'Capas', panelTitle: 'Capas', icon: 'functionalities/consolidated' },
];

const PANEL_TABS = [
  { id: 'templates', label: 'Templates' },
  { id: 'workspace', label: 'Mesa de trabajo' },
];

const CanvasSidePanel = () => {
  const { playAllAnimations, activeRailTab, setActiveRailTab } = useCanvasEditor();
  const [activeTab, setActiveTab] = useState('templates');
  const [searchValue, setSearchValue] = useState('');

  const activeItem = RAIL_ITEMS.find((item) => item.id === activeRailTab) ?? RAIL_ITEMS[0];

  const renderActivePanel = () => {
    switch (activeItem.id) {
      case 'template':
        return (
          <>
            <div className="flex w-full border-b border-gray-300">
              {PANEL_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 pb-2 text-sm font-semibold text-center border-b-4 cursor-pointer ${
                    activeTab === tab.id ? 'text-gray-900 border-[#FFCC00]' : 'text-[#595959] border-transparent'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'templates' ? (
              <CanvasTemplatesTab search={searchValue} />
            ) : (
              <CanvasWorkspaceTab />
            )}
          </>
        );
      case 'sku':
        return <CanvasSkusTab search={searchValue} />;
      case 'texto':
        return <CanvasTextTab search={searchValue} />;
      case 'animacion':
        return <CanvasAnimationTab />;
      case 'capas':
        return <CanvasLayersTab />;
      default:
        return <p className="text-sm text-gray-500">Próximamente.</p>;
    }
  };

  return (
    <div className="flex h-full">
      <div className="w-16 h-full shrink-0 flex flex-col items-center gap-1 bg-[#fafafa]">
        {RAIL_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActiveRailTab(item.id)}
            className={`flex flex-col items-center gap-1 w-full py-2 text-[11px] cursor-pointer ${
              activeRailTab === item.id ? 'bg-[#FFCC00] text-black' : 'text-[#595959]'
            }`}
          >
            <span className="w-6 h-6 flex items-center justify-center text-2xl">
              <Icon name={item.icon} />
            </span>
            {item.label}
          </button>
        ))}
      </div>

      <div className="w-96 flex flex-col gap-4 p-4 border-r border-gray-200 overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{activeItem.panelTitle}</h2>
          {activeItem.id === 'animacion' && (
            <Button
              label="Vista previa"
              rightIcon="media/play"
              variant="primary"
              className="cursor-pointer rounded-full"
              onClick={playAllAnimations}
            />
          )}
        </div>

        {activeItem.id !== 'animacion' && activeItem.id !== 'capas' && (
          <SearchBar
            id="canvas-side-panel-search"
            name="Buscar"
            variant="default"
            leftIcon="search"
            className="w-full rounded-md"
            placeholder="Buscar"
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            onClear={() => setSearchValue('')}
          />
        )}

        {renderActivePanel()}
      </div>
    </div>
  );
};

export default CanvasSidePanel;
